import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  getThumbnailNeighborPages,
  orderThumbnailPages,
  renderPdfThumbnailToCanvas,
  THUMB_ITEM_MIN_HEIGHT,
  THUMB_MAX_HEIGHT,
  THUMB_MAX_WIDTH
} from "./pdfThumbnailRender";

type PDFViewerThumbnailsProps = {
  classes: Record<string, string>;
  pdfDoc: any;
  totalPages: number;
  currentPage: number;
  onPageSelect: (pageNo: number) => void;
};

export const PDFViewerThumbnails = (props: PDFViewerThumbnailsProps) => {
  const { classes, pdfDoc, totalPages, currentPage, onPageSelect } = props;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const canvasRefs = useRef<{ [page: number]: HTMLCanvasElement | null }>({});
  const itemRefs = useRef<{ [page: number]: HTMLButtonElement | null }>({});
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const pendingPagesRef = useRef<Set<number>>(new Set());
  const renderTaskRef = useRef<{ cancel?: () => void } | null>(null);
  const renderRequestIdRef = useRef(0);
  const isRenderingRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  const [renderedPages, setRenderedPages] = useState<Record<number, boolean>>({});

  currentPageRef.current = currentPage;

  const cancelActiveThumbnailRender = () => {
    renderRequestIdRef.current += 1;
    renderTaskRef.current?.cancel?.();
    renderTaskRef.current = null;
  };

  const markPageRendered = (pageNo: number) => {
    renderedPagesRef.current.add(pageNo);
    setRenderedPages((prev) => ({ ...prev, [pageNo]: true }));
  };

  const queueThumbnailRenders = (pageNumbers: number[]) => {
    if (!pdfDoc || !totalPages) return;

    pageNumbers.forEach((pageNo) => {
      if (pageNo >= 1 && pageNo <= totalPages && !renderedPagesRef.current.has(pageNo)) {
        pendingPagesRef.current.add(pageNo);
      }
    });

    if (isRenderingRef.current) return;
    isRenderingRef.current = true;
    const requestId = renderRequestIdRef.current;

    const processQueue = async () => {
      while (pendingPagesRef.current.size > 0) {
        if (requestId !== renderRequestIdRef.current) break;

        const batch = Array.from(pendingPagesRef.current);
        pendingPagesRef.current.clear();
        const orderedPages = orderThumbnailPages(batch, currentPageRef.current);

        for (const pageNo of orderedPages) {
          if (requestId !== renderRequestIdRef.current) break;
          if (renderedPagesRef.current.has(pageNo)) continue;

          const canvas = canvasRefs.current[pageNo];
          if (!canvas) {
            pendingPagesRef.current.add(pageNo);
            continue;
          }

          try {
            const rendered = await renderPdfThumbnailToCanvas(
              pdfDoc,
              pageNo,
              canvas,
              () => requestId !== renderRequestIdRef.current
            );
            if (rendered) {
              markPageRendered(pageNo);
            }
          } catch {
            // Skip pages that fail thumbnail render.
          }
        }
      }

      if (requestId === renderRequestIdRef.current) {
        isRenderingRef.current = false;
        if (pendingPagesRef.current.size > 0) {
          queueThumbnailRenders([]);
        }
      }
    };

    void processQueue();
  };

  useEffect(() => {
    if (!pdfDoc || !totalPages) return;

    renderedPagesRef.current = new Set();
    pendingPagesRef.current = new Set();
    setRenderedPages({});
    cancelActiveThumbnailRender();
    isRenderingRef.current = false;
    queueThumbnailRenders(getThumbnailNeighborPages(currentPage, totalPages));

    return () => {
      cancelActiveThumbnailRender();
      isRenderingRef.current = false;
      pendingPagesRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, totalPages]);

  useEffect(() => {
    if (!pdfDoc || !totalPages) return;
    queueThumbnailRenders(getThumbnailNeighborPages(currentPage, totalPages));
    itemRefs.current[currentPage]?.scrollIntoView({ block: "nearest", behavior: "auto" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pdfDoc, totalPages]);

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root || !pdfDoc || !totalPages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visiblePages = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => Number((entry.target as HTMLElement).dataset.pageNo))
          .filter((pageNo) => !Number.isNaN(pageNo));

        if (visiblePages.length) {
          queueThumbnailRenders(visiblePages);
        }
      },
      {
        root,
        rootMargin: "240px 0px",
        threshold: 0.01
      }
    );

    const observeThumbnails = () => {
      for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
        const item = itemRefs.current[pageNo];
        if (item) observer.observe(item);
      }
    };

    observeThumbnails();
    const rafId = requestAnimationFrame(observeThumbnails);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, totalPages]);

  return (
    <div ref={scrollContainerRef} className={classes.pdfSidebarPanelBody}>
      <Typography variant="subtitle2" className={classes.pdfSidebarTitle}>
        Pages
      </Typography>
      <div className={classes.pdfThumbnailList}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNo) => (
          <button
            key={`thumb-${pageNo}`}
            type="button"
            data-page-no={pageNo}
            ref={(el) => {
              itemRefs.current[pageNo] = el;
            }}
            className={`${classes.pdfThumbnailItem} ${
              pageNo === currentPage ? classes.pdfThumbnailActive : ""
            }`}
            onClick={() => onPageSelect(pageNo)}
            aria-label={`Go to page ${pageNo}`}
            aria-current={pageNo === currentPage ? "page" : undefined}
            style={{ minHeight: THUMB_ITEM_MIN_HEIGHT }}
          >
            <canvas
              ref={(el) => {
                canvasRefs.current[pageNo] = el;
              }}
              className={classes.pdfThumbnailCanvas}
              style={{
                width: THUMB_MAX_WIDTH,
                height: THUMB_MAX_HEIGHT,
                backgroundColor: renderedPages[pageNo] ? undefined : "#f8f9fa"
              }}
            />
            <Typography variant="caption" className={classes.pdfThumbnailLabel}>
              {renderedPages[pageNo] ? pageNo : "…"}
            </Typography>
          </button>
        ))}
      </div>
    </div>
  );
};
